import { jsx } from "panel";
import { InputEl } from "common/components/input";
import { InputSize, InputType, ValidationStatus } from "common/components/input/types";
import { EntryPart, EntryType, RowLabelType } from "common/query-builder/query-entry-section/types";
import { SelectEl } from "common/components/select";
import { ButtonEl } from "common/components/button";
import { SelectTheme } from "common/components/select/constants";
import { generateCountCalculationMenuOptions } from "common/query-builder/query-block/count-menu/util";
import { ButtonSize, ButtonTheme, ButtonType } from "common/components/button/constants";
import { DisplaySize, Placement } from "common/components/tooltip/types";
import { getSelectedCalculation, getSelectedCalculationLabel } from "common/query-builder/query-entry-section/util";
import { RowSelector } from "common/query-builder/query-entry-section/components/selector";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section";
import { SkeletonListRowSize } from "common/components/skeleton-list/types";
import { SkeletonListEl } from "common/components/skeleton-list";
import { getEditCapability } from "common/report/util/saved-entities";
import { SplitButtonEl } from "common/components/split-button";
import { MultiAttributionAggregator } from "common/query-builder/query-entry-section/components/aggregations";
import { CountUsersOnceState } from "common/report/constants";
import { BaseEntryRowEl } from "../base/template";
export function FormulaEntryRowEl(entryRowProps) {
  var _a;
  const { section, entry, index } = entryRowProps;
  const calculation = getSelectedCalculation(((_a = this.getResolvedFormulaShowClause()) == null ? void 0 : _a.measurement) || {});
  const { isExpanded } = this.entry;
  const isCollapsedSavedFormula = this.isSavedFormula() && !isExpanded;
  const formulaError = this.getFormulaError();
  const selectorRenderer = () => {
    return this.isSelectingFormula() === false && this.isUnsavedFormula() ? /* @__PURE__ */ jsx("div", {
      sel: `.mp-query-entry-selector.formula-name`
    }, this.getName()) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(RowSelector, {
      el: section,
      entry,
      index,
      isPlaceholder: this.isPlaceholder(),
      handleSelect: (ev) => this.handleSelect(ev.detail.item),
      handleCreate: () => this.createNewInlineEntry()
    }), isCollapsedSavedFormula && /* @__PURE__ */ jsx(MultiAttributionAggregator, {
      el: section,
      index
    }));
  };
  const renderReferencedEntries = () => {
    const referencedEntries = this.getReferencedEntries();
    if (!referencedEntries) {
      return null;
    }
    const nestedSectionAttrs = section.helpers.getNestedSectionAttrs();
    return /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      attrs: {
        ...nestedSectionAttrs,
        "entry-type": EntryType.Event,
        elref: `subEntry`,
        "add-entry-button-label": `Metric`,
        "add-entry-button-tooltip-text": `Add a metric which can be referenced in the formula`,
        "show-top-events": false,
        "count-users-once-state": section.getMultiAttribution(index) ? CountUsersOnceState.Required : section.attr(`count-users-once-state`),
        "date-range": section.attr(`date-range`),
        "date-unit": section.attr(`date-unit`),
        "is-referenced-metrics": true,
        "metrics-mode": true,
        "row-types": this.getReferencedMetricsRowTypes(),
        "row-label-type": RowLabelType.Alpha,
        "row-index": index,
        "row-draggable": true,
        "track-properties": {
          ...nestedSectionAttrs[`track-properties`] || {},
          sectionName: `referenced-metrics`
        },
        entries: referencedEntries
      },
      on: {
        update: (ev) => this.onReferencedMetricsUpdate(ev)
      }
    });
  };
  const renderEditControls = () => {
    if (this.isModifiedFormula() === false || this.isLoadingModel) {
      return null;
    }
    const canEdit = getEditCapability(this.model.value).enableEdit;
    const saveDisabledMessage = this.getSaveDisabledMessage();
    return /* @__PURE__ */ jsx("div", {
      sel: ".formula-modified-buttons"
    }, /* @__PURE__ */ jsx(ButtonEl, {
      attrs: {
        theme: ButtonTheme.Secondary,
        size: ButtonSize.Medium
      },
      on: {
        click: () => this.handleResetModifications()
      }
    }, "Reset"), /* @__PURE__ */ jsx(SplitButtonEl, {
      params: {
        disabled: !this.isSaveEnabled(),
        theme: ButtonTheme.Secondary,
        sections: canEdit && [
          {
            items: [
              { label: `Save for Everyone`, value: `save` },
              { label: `Save as New`, value: `clone` }
            ]
          }
        ],
        ...saveDisabledMessage && { tooltip: { text: saveDisabledMessage, size: DisplaySize.Medium } },
        handleClick: () => this.handleSaveModifications(canEdit === false),
        handleSelectChange: (ev) => {
          const action = ev.detail.item.value;
          const saveAsNew = action === `clone`;
          this.handleSaveModifications(saveAsNew);
        }
      }
    }, canEdit ? `Save for Everyone` : `Save as New`));
  };
  const suffixRenderer = this.isSelectingFormula() || isCollapsedSavedFormula ? null : () => {
    if (this.isLoadingModel) {
      return /* @__PURE__ */ jsx("div", {
        sel: ".mp-query-entry-row.for-formula-definition"
      }, /* @__PURE__ */ jsx(SkeletonListEl, {
        attrs: { "num-rows": 4, "row-size": SkeletonListRowSize.Large },
        sel: ".mp-formulas-menu-skeleton-list"
      }));
    }
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-query-entry-row.for-formula-definition"
    }, /* @__PURE__ */ jsx(InputEl, {
      sel: `.formula-definition`,
      attrs: {
        elref: `formulaDefinition`,
        type: InputType.Text,
        size: InputSize.Medium,
        placeholder: `eg. (A+B)/(A-B) * 100`,
        "auto-focus": section.state.openEntry.index === index && section.state.openEntry.part === EntryPart.Value,
        ...formulaError && {
          "validation-status": ValidationStatus.Invalid,
          "error-message": formulaError
        }
      },
      props: {
        value: this.getDefinition()
      },
      on: {
        keydown: (ev) => this.handleDefinitionKeydown(ev),
        input: (ev) => this.handleDefinitionInput(ev),
        blur: (ev) => this.handleDefinitionBlur(ev),
        focus: () => this.handleDefinitionFocus()
      }
    }), renderReferencedEntries(), calculation.calculationType && /* @__PURE__ */ jsx("div", {
      sel: ".calculation-selector"
    }, /* @__PURE__ */ jsx(SelectEl, {
      key: "calculation-selector",
      attrs: {
        elref: `calculationSelector`,
        label: getSelectedCalculationLabel(calculation, section.attr(`date-unit`)),
        "disable-search": true,
        "menu-width": 180,
        "track-name": section.attr(`track-name`),
        theme: SelectTheme.Tertiary,
        "disable-tooltip": true
      },
      on: {
        change: (ev) => this.handleCalculationSelected(ev.detail.item.value),
        menuChange: (ev) => section.helpers.onSelectMenuChange(ev, index, section.helpers.EntryPart.Calculation)
      },
      props: {
        sections: generateCountCalculationMenuOptions({
          selectedPlotStyle: section.attr(`plot-style`),
          selectedCalculation: calculation,
          selectedDateUnit: section.attr(`date-unit`)
        })
      }
    }), /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".remove-button",
      attrs: {
        theme: ButtonTheme.Tertiary,
        size: ButtonSize.Inline,
        type: ButtonType.Destructive,
        tooltip: {
          placement: Placement.RightCenter,
          size: DisplaySize.Small,
          text: `Remove`
        },
        icon: `x`
      },
      on: {
        click: () => this.handleCalculationRemoved()
      }
    })), renderEditControls(), /* @__PURE__ */ jsx(MultiAttributionAggregator, {
      el: section,
      index
    }));
  };
  const actionsRenderer = this.isUnsavedFormula() || this.isModifiedFormula() && isExpanded && !this.getSaveDisabledMessage() ? null : () => /* @__PURE__ */ jsx(ButtonEl, {
    sel: ".expand-control",
    attrs: {
      icon: isExpanded ? `collapse-diagonal` : `expand`,
      theme: ButtonTheme.Tertiary,
      size: ButtonSize.Medium,
      tooltip: {
        text: isExpanded ? `Collapse` : `Expand`
      }
    },
    on: {
      click: (_ev) => this.handleToggleExpandRowClick()
    }
  });
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    index,
    el: section,
    entry,
    isPlaceholder: false,
    rowRenderer: {
      actionsRenderer,
      selectorRenderer,
      suffixRenderer
    }
  });
}
